import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { useNetwork } from '@/providers/NetworkProvider';
import '@/styles/effects.css';

interface Transaction {
  hash: string;
  status: 'pending' | 'confirmed' | 'failed';
  description: string;
  timestamp: number;
  gasUsed?: string;
  blockNumber?: number;
}

export const TransactionStatus: React.FC = () => {
  const { currentNetwork } = useNetwork();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const pollTimers = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const failTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const explorerBase =
    currentNetwork === 'mainnet'
      ? 'https://blockscout.com/etc/mainnet/tx/'
      : 'https://blockscout.com/etc/mordor/tx/';

  const clearMonitors = (hash: string) => {
    const poll = pollTimers.current.get(hash);
    if (poll) {
      clearInterval(poll);
      pollTimers.current.delete(hash);
    }
    const fail = failTimers.current.get(hash);
    if (fail) {
      clearTimeout(fail);
      failTimers.current.delete(hash);
    }
  };

  const updateTransactionStatus = (
    hash: string,
    status: 'confirmed' | 'failed',
    receipt?: ethers.TransactionReceipt
  ) => {
    clearMonitors(hash);
    setTransactions((prev) =>
      prev.map((tx) =>
        tx.hash === hash
          ? {
              ...tx,
              status,
              gasUsed: receipt?.gasUsed?.toString(),
              blockNumber: receipt?.blockNumber,
            }
          : tx
      )
    );

    if (status === 'confirmed') {
      toast.success('Transaction confirmed');
    } else {
      toast.error('Transaction failed');
    }
  };

  const monitorTransaction = async (hash: string) => {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const receipt = await provider.getTransactionReceipt(hash);
      if (receipt) {
        updateTransactionStatus(hash, receipt.status === 1 ? 'confirmed' : 'failed', receipt);
        return;
      }

      const pollInterval = setInterval(async () => {
        try {
          const pollReceipt = await provider.getTransactionReceipt(hash);
          if (pollReceipt) {
            updateTransactionStatus(
              hash,
              pollReceipt.status === 1 ? 'confirmed' : 'failed',
              pollReceipt
            );
          }
        } catch (error) {
          console.error('Error polling transaction:', error);
        }
      }, 2000);
      pollTimers.current.set(hash, pollInterval);

      const failTimer = setTimeout(() => {
        updateTransactionStatus(hash, 'failed');
      }, 300000);
      failTimers.current.set(hash, failTimer);
    } catch (error) {
      console.error('Error monitoring transaction:', error);
      updateTransactionStatus(hash, 'failed');
    }
  };

  const addTransaction = (hash: string, description: string) => {
    const newTx: Transaction = {
      hash,
      status: 'pending',
      description,
      timestamp: Date.now(),
    };
    setTransactions((prev) => [newTx, ...prev.slice(0, 9)]);
    void monitorTransaction(hash);
  };

  const formatTimeAgo = (timestamp: number): string => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Transaction hash copied');
  };

  useEffect(() => {
    (window as any).addTransaction = addTransaction;
    return () => {
      delete (window as any).addTransaction;
      pollTimers.current.forEach((id) => clearInterval(id));
      failTimers.current.forEach((id) => clearTimeout(id));
      pollTimers.current.clear();
      failTimers.current.clear();
    };
  }, []);

  if (transactions.length === 0) {
    return (
      <div className="tx-monitor">
        <div className="tx-monitor-head">TRANSACTION MONITOR</div>
        <div className="tx-empty">No recent transactions in system</div>
      </div>
    );
  }

  return (
    <div className="tx-monitor">
      <div className="tx-monitor-head">RECENT TRANSACTIONS</div>
      <div className="tx-list">
        {transactions.map((tx) => (
          <div key={tx.hash} className={`tx-strip tx-${tx.status}`}>
            <span className="tx-status-icon" aria-hidden="true">
              {tx.status === 'pending' ? (
                <span className="tx-spinner" />
              ) : tx.status === 'confirmed' ? (
                '✓'
              ) : (
                '✗'
              )}
            </span>

            <div className="tx-body">
              <div className="tx-desc">{tx.description}</div>
              <div className="tx-meta">
                <button
                  type="button"
                  className="tx-hash"
                  onClick={() => copyToClipboard(tx.hash)}
                  title="Copy hash"
                >
                  {tx.hash.slice(0, 8)}…{tx.hash.slice(-6)}
                </button>
                <span className="tx-time">{formatTimeAgo(tx.timestamp)}</span>
              </div>
            </div>

            <a
              className="tx-link"
              href={`${explorerBase}${tx.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View on explorer"
            >
              ↗
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};
